import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  describeRawValue,
  isExpandableValue,
  rawValueType,
  safeStringify,
  unlistedKeys
} from './detailFormat'

// Past this the JSON is cut, so a 7,000-sample heart-rate array can't freeze
// the tab. The full value is still one console.log away.
const MAX_JSON_LENGTH = 100000

const RawValue = ({ fieldKey, value }) => {
  const [isOpen, setIsOpen] = useState(false)
  const preview = describeRawValue(fieldKey, value)

  if (rawValueType(value) === 'file') {
    return (
      <a href={value.url()} target="_blank" rel="noopener noreferrer">
        {preview}
      </a>
    )
  }

  if (!isExpandableValue(fieldKey, value)) {
    return <span>{preview}</span>
  }

  const json = isOpen ? safeStringify(value, 2) : ''
  return (
    <div>
      <span>{preview}</span>{' '}
      <button
        type="button"
        className="btn btn-link btn-sm p-0 align-baseline"
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? 'hide' : 'view'}
      </button>
      {isOpen && (
        <pre className="detail-json mt-1 mb-0">
          {json.length > MAX_JSON_LENGTH
            ? json.slice(0, MAX_JSON_LENGTH) + '\n… (truncated)'
            : json}
        </pre>
      )}
    </div>
  )
}

RawValue.propTypes = {
  fieldKey: PropTypes.string.isRequired,
  value: PropTypes.any
}

// Every field stored on a Parse object, collapsed by default. Fields the
// structured view above doesn't render are listed in the summary, so a field
// the backend starts writing shows up here before anyone builds a UI for it.
const RawFieldsComponent = ({ object, shownKeys, label }) => {
  if (!object) {
    return null
  }
  const attributes = object.attributes || {}
  const keys = Object.keys(attributes).sort()
  const unlisted = unlistedKeys(attributes, shownKeys)
  const unlistedSet = new Set(unlisted)

  return (
    <details className="detail-raw mb-2">
      <summary>
        <b>{label || object.className}</b>{' '}
        <small className="text-muted">
          {object.id} · {keys.length} stored fields
          {unlisted.length > 0 &&
            ` · not shown above: ${unlisted.join(', ')}`}
        </small>
      </summary>
      <table className="table table-sm detail-raw-table mt-2 mb-0">
        <tbody>
          {keys.map((key) => (
            <tr key={key}>
              <td className="detail-raw-key">
                <code>{key}</code>
                {unlistedSet.has(key) && (
                  <span className="badge text-bg-warning ms-1">not shown above</span>
                )}
              </td>
              <td className="text-muted detail-raw-type">
                {rawValueType(attributes[key])}
              </td>
              <td>
                <RawValue fieldKey={key} value={attributes[key]} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  )
}

RawFieldsComponent.propTypes = {
  object: PropTypes.object,
  shownKeys: PropTypes.arrayOf(PropTypes.string),
  label: PropTypes.string
}

export default RawFieldsComponent
